import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { translations, languages } from './translations';

// Import all page components
import PersonaSelectionPage from './components/PersonaSelectionPage';
import GoalSelectionPage from './components/GoalSelectionPage';
import EmailInputPage from './components/EmailInputPage';
import LanguageSelectionPage from './components/LanguageSelectionPage';
import PersonalDetailsPage from './components/PersonalDetailsPage';
import MembershipPlanPage from './components/MembershipPlanPage';
import TermsConditionsPage from './components/TermsConditionsPage';
import DigitalSignaturePage from './components/DigitalSignaturePage';
import EmailConfirmationPage from './components/EmailConfirmationPage';
import SendConfirmationPage from './components/SendConfirmationPage';
import SuccessScreenPage from './components/SuccessScreenPage';
import Dashboard from './components/Dashboard';
import TrainerLoginPage from './components/TrainerLoginPage';

function App() {
  // State management for the entire registration flow
  const [currentLanguage, setCurrentLanguage] = useState('Dutch');
  const [registrationType, setRegistrationType] = useState('');
  const [personalDetails, setPersonalDetails] = useState({
    invoiceEmail: '',
    email: '',
    persona: '',
    goals: [],
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    dateOfBirth: '',
  });
  const [membershipPlan, setMembershipPlan] = useState({
    package: '',
    isSixWeek: false,
    price: '',
    sessionDuration: '',
    startDate: '',
    location: '',
  });
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [alsoSignPtTerms, setAlsoSignPtTerms] = useState(false);
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [trainerSession, setTrainerSession] = useState(() => {
    const storedSession = localStorage.getItem('trainerSession');
    return storedSession ? JSON.parse(storedSession) : null;
  });
  
  // Function to reset all registration data
  const resetRegistrationData = () => {
    setRegistrationType('');
    setPersonalDetails({
      email: '',
      persona: '',
      goals: [],
      firstName: '',
      lastName: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      dateOfBirth: '',
    });
    setMembershipPlan({
      isSixWeek: '',
      package: '',
      price: '',
      sessionDuration: '',
      startDate: '',
      location: '',
      futureAgreed: ''
    });
    setTermsAgreed(false);
    setAlsoSignPtTerms(false);
    setSignatureDataURL('');
    setEmailConfirmed(false);
  };

  const handleTrainerLogin = (session) => {
    setTrainerSession(session);
    localStorage.setItem('trainerSession', JSON.stringify(session));
  };

  const handleTrainerLogout = () => {
    setTrainerSession(null);
    localStorage.removeItem('trainerSession');
    resetRegistrationData();
  };
  
  // Translation helper function
  const t = (key) => {
    return translations[currentLanguage][key] || key;
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            trainerSession ? (
              <Navigate to="/" replace />
            ) : (
              <TrainerLoginPage onLoginSuccess={handleTrainerLogin} />
            )
          }
        />
        <Route path="/" 
          element={
          trainerSession ? (
            <Dashboard 
              registrationType={registrationType} 
              setRegistrationType={setRegistrationType}
              resetRegistrationData={resetRegistrationData}
              trainerSession={trainerSession}
              onLogout={handleTrainerLogout}
              t={t}
              />
          ) : (
            <Navigate to="/login" replace />
          )
            }
        />
        <Route 
          path="/persona-selection" 
          element={
            trainerSession ? (
              <PersonaSelectionPage 
                personalDetails={personalDetails}
                setPersonalDetails={setPersonalDetails}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/goal-selection" 
          element={
            trainerSession ? (
              <GoalSelectionPage 
                personalDetails={personalDetails}
                setPersonalDetails={setPersonalDetails}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/email-input" 
          element={
            trainerSession ? (
              <EmailInputPage 
                personalDetails={personalDetails}
                setPersonalDetails={setPersonalDetails}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/language-selection" 
          element={
            trainerSession ? (
              <LanguageSelectionPage 
                currentLanguage={currentLanguage}
                setCurrentLanguage={setCurrentLanguage}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/personal-details" 
          element={
            trainerSession ? (
              <PersonalDetailsPage 
                personalDetails={personalDetails}
                setPersonalDetails={setPersonalDetails}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/membership-plan" 
          element={
            trainerSession ? (
              <MembershipPlanPage 
                membershipPlan={membershipPlan}
                setMembershipPlan={setMembershipPlan}
                registrationType={registrationType}
                trainerSession={trainerSession}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/terms-conditions" 
          element={
            trainerSession ? (
              <TermsConditionsPage 
                currentLanguage={currentLanguage}
                personalDetails={personalDetails}
                registrationType={registrationType}
                termsAgreed={termsAgreed}
                setTermsAgreed={setTermsAgreed}
                alsoSignPtTerms={alsoSignPtTerms}
                setAlsoSignPtTerms={setAlsoSignPtTerms}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/digital-signature" 
          element={
            trainerSession ? (
              <DigitalSignaturePage 
                signatureDataURL={signatureDataURL}
                setSignatureDataURL={setSignatureDataURL}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/email-confirmation" 
          element={
            trainerSession ? (
              <EmailConfirmationPage 
                personalDetails={personalDetails}
                emailConfirmed={emailConfirmed}
                setEmailConfirmed={setEmailConfirmed}
                setPersonalDetails={setPersonalDetails}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/send-confirmation" 
          element={
            trainerSession ? (
              <SendConfirmationPage 
                personalDetails={personalDetails}
                registrationType={registrationType}
                membershipPlan={membershipPlan}
                signatureDataURL={signatureDataURL}
                termsAgreed={termsAgreed}
                alsoSignPtTerms={alsoSignPtTerms}
                currentLanguage={currentLanguage}
                trainerId={trainerSession?.trainerId}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route 
          path="/success" 
          element={
            trainerSession ? (
              <SuccessScreenPage 
                resetRegistrationData={resetRegistrationData}
                onLogout={handleTrainerLogout}
                t={t}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
